import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import { useAuth } from "@/context/AuthContext";
import { getCategories } from "@/services/publicApi";
import {
  createAdminSubcategory,
  deleteAdminSubcategory,
  getSubcategories,
  updateAdminSubcategory,
} from "@/services/adminApi";
import type { Category, Subcategory } from "@/types";

const slugify = (value: string) =>
  value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminSubcategories() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Subcategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Subcategory | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getSubcategories(token), getCategories()])
      .then(([subs, cats]) => {
        if (!active) return;
        setRows(subs);
        setCategories(cats);
        if (cats[0]) setCategoryId(String(cats[0].id));
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => !q || r.name.toLowerCase().includes(q) || r.categoryName.toLowerCase().includes(q));
  }, [rows, query]);

  const resetForm = () => {
    setName("");
    setEditingId(null);
    setShowForm(false);
    if (categories[0]) setCategoryId(String(categories[0].id));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !categoryId) return;
    setSaving(true);
    const parentName = categories.find((c) => String(c.id) === categoryId)?.name ?? "";

    if (editingId) {
      const ok = await updateAdminSubcategory(
        editingId,
        { name: trimmed, slug: slugify(trimmed), categoryId },
        token,
      );
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that subcategory. Please try again.");
        return;
      }
      setRows((prev) =>
        prev.map((r) => (r.id === editingId ? { ...r, name: trimmed, slug: slugify(trimmed), categoryId, categoryName: parentName } : r)),
      );
      flash(`Subcategory “${trimmed}” updated.`);
    } else {
      const created = await createAdminSubcategory({ name: trimmed, slug: slugify(trimmed), categoryId }, token);
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that subcategory. Please try again.");
        return;
      }
      setRows((prev) => [...prev, { ...created, categoryName: created.categoryName || parentName }]);
      flash(`Subcategory “${trimmed}” created.`);
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: Subcategory) => {
    setEditingId(row.id);
    setName(row.name);
    setCategoryId(String(row.categoryId));
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleStatus = async (row: Subcategory) => {
    const next = row.status === "active" ? "hidden" : "active";
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    const ok = await updateAdminSubcategory(row.id, { isActive: next === "active" }, token);
    if (!ok) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)));
      flashError("Couldn't update visibility. Please try again.");
      return;
    }
    flash("Visibility updated.");
  };

  const remove = async (row: Subcategory) => {
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminSubcategory(row.id, token);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that subcategory. Please try again.");
      return;
    }
    if (editingId === row.id) resetForm();
    flash("Subcategory removed.");
  };

  const columns: DataColumn<Subcategory>[] = [
    {
      key: "name",
      header: "Subcategory",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
            <i className="ri-node-tree"></i>
          </span>
          <div>
            <p className="font-semibold text-foreground-950">{r.name}</p>
            <p className="text-xs text-foreground-500">/{r.slug}</p>
          </div>
        </div>
      ),
    },
    { key: "categoryName", header: "Parent category", render: (r) => <span className="text-foreground-700">{r.categoryName}</span>, nowrap: true },
    { key: "businessCount", header: "Listings", align: "right", render: (r) => <span className="font-semibold text-foreground-900">{r.businessCount}</span>, nowrap: true },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => toggleStatus(r)}
            className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            <i className={r.status === "active" ? "ri-eye-off-line" : "ri-eye-line"}></i>
            {r.status === "active" ? "Hide" : "Show"}
          </button>
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit subcategory"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete subcategory"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Subcategories" path="/admin/subcategories" />

      <AdminPageHeader
        icon="ri-node-tree"
        title="Subcategories"
        subtitle="Fine-grained groupings nested under each top-level category."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New subcategory"}
            </button>
          </>
        }
      />

      {showForm && (
        <form onSubmit={submit} className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label htmlFor="subName" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Name</label>
            <input
              id="subName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Pet Grooming"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div className="lg:w-64">
            <label htmlFor="subParent" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Parent category</label>
            <select
              id="subParent"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            {editingId ? "Save changes" : "Save"}
          </button>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search subcategories…" />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load subcategories. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="760px" />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this subcategory?"
        message={pendingDelete ? `“${pendingDelete.name}” will be permanently removed. This cannot be undone.` : ""}
        confirmLabel="Delete subcategory"
        busy={deleting}
        onConfirm={async () => {
          const target = pendingDelete;
          if (!target) return;
          setDeleting(true);
          await remove(target);
          setDeleting(false);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}